import React, {createContext, useContext, useState, useCallback} from 'react';
import {Toast, ToastType} from '.././toast';
import {registerToast} from ".././toast-store";

export interface ToastItem {
    id: string;
    type: ToastType;
    title: string;
    message: string;
}

interface ToastContextType {
    toasts: ToastItem[];
    addToast: (toast: Omit<ToastItem, 'id'>) => void;
    removeToast: (id: string) => void;
    clearToasts: () => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const useToast = () => {
    const context = useContext(ToastContext);
    if (!context) {
        throw new Error('useToast must be used within ToastProvider');
    }
    return context;
};

interface ToastProviderProps {
    children: React.ReactNode;
}

export const ToastProvider: React.FC<ToastProviderProps> = ({children}) => {
    const [toasts, setToasts] = useState<ToastItem[]>([]);

    const addToast = useCallback((toast: Omit<ToastItem, 'id'>) => {
        const id = Date.now().toString();
        const newToast: ToastItem = {...toast, id};
        setToasts(prev => [...prev, newToast]);

        // Auto remove after 5 seconds
        setTimeout(() => {
            removeToast(id);
        }, 5000);
    }, []);

    const removeToast = useCallback((id: string) => {
        setToasts(prev => prev.filter(toast => toast.id !== id));
    }, []);

    const clearToasts = useCallback(() => {
        setToasts([]);
    }, []);

    React.useEffect(() => {
        registerToast(addToast);
    }, [addToast]);


    return (
        <ToastContext.Provider value={{toasts, addToast, removeToast, clearToasts}}>
            {children}
            <ToastContainer/>
        </ToastContext.Provider>
    );
};

const ToastContainer: React.FC = () => {
    const {toasts, removeToast} = useToast();

    return (
        <div className="fixed top-4 right-4 z-[9999] space-y-2">
            {toasts.map(toast => (
                <Toast
                    key={toast.id}
                    id={toast.id}
                    type={toast.type}
                    title={toast.title}
                    message={toast.message}
                    onClose={removeToast}
                />
            ))}
        </div>
    );
}; 
